import { useQuery, useQueryClient } from '@tanstack/react-query';

import { GlobalAccount, GUID, OpportunityStatus } from '@ndvr/customer-api';

export enum OpportunityLayout {
    LIST = 'list',
    KANBAN = 'kanban',
    FUNNEL = 'funnel',
}

export enum OpportunitySortOption {
    NAME = 'name',
    HOUSEHOLD = 'household',
    STATUS = 'status',
    VALUE = 'value',
    CREATED_DATE = 'createdDate',
}

export interface OpportunityViewState {
    layout: OpportunityLayout;
    sortOption: OpportunitySortOption;
    sortDirection: 'asc' | 'desc';
    selectedHouseholds: GUID<GlobalAccount>[];
    selectedStatuses: OpportunityStatus[];
    minimumValue: number | null;
}

const OPPORTUNITY_VIEW_STATE_KEY = 'opportunity-view-state';

const DEFAULT_OPPORTUNITY_VIEW_STATE: OpportunityViewState = {
    layout: OpportunityLayout.LIST,
    sortOption: OpportunitySortOption.CREATED_DATE,
    sortDirection: 'desc',
    selectedHouseholds: [],
    selectedStatuses: [],
    minimumValue: null,
};

const getLocalStorageOpportunityViewState = (): OpportunityViewState => {
    try {
        const stored = JSON.parse(localStorage.getItem(OPPORTUNITY_VIEW_STATE_KEY) ?? '{}');
        return {
            ...DEFAULT_OPPORTUNITY_VIEW_STATE,
            ...stored,
        };
    } catch {
        return DEFAULT_OPPORTUNITY_VIEW_STATE;
    }
};

const useOpportunityViewStateQuery = () => {
    return useQuery({
        queryKey: [OPPORTUNITY_VIEW_STATE_KEY],
        queryFn: getLocalStorageOpportunityViewState,
        gcTime: 0,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useOpportunityViewState = (): {
    opportunityViewState: OpportunityViewState;
    setOpportunityViewState: (partialState: Partial<OpportunityViewState>) => void;
} => {
    const queryClient = useQueryClient();
    const opportunityViewStateQuery = useOpportunityViewStateQuery();

    return {
        opportunityViewState: opportunityViewStateQuery.data ?? DEFAULT_OPPORTUNITY_VIEW_STATE,
        setOpportunityViewState: (partialState: Partial<OpportunityViewState>) => {
            localStorage.setItem(
                OPPORTUNITY_VIEW_STATE_KEY,
                JSON.stringify({
                    ...(opportunityViewStateQuery.data ?? DEFAULT_OPPORTUNITY_VIEW_STATE),
                    ...partialState,
                }),
            );
            queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_VIEW_STATE_KEY] });
        },
    };
};

const OPPORTUNITY_OVERLAY_STATE_KEY = 'opportunity-overlay-state';

export enum OpportunitySheetType {
    VIEW = 'view',
}

export interface OpportunityOverlayState {
    opportunityGuid: string | null;
    sheetType: OpportunitySheetType | null;
    deleteOpportunityGuid: string | null;
}

const DEFAULT_OPPORTUNITY_OVERLAY_STATE: OpportunityOverlayState = {
    opportunityGuid: null,
    sheetType: null,
    deleteOpportunityGuid: null,
};

const useOpportunityOverlayQuery = () =>
    useQuery<OpportunityOverlayState>({
        queryKey: [OPPORTUNITY_OVERLAY_STATE_KEY],
        queryFn: () => DEFAULT_OPPORTUNITY_OVERLAY_STATE,
        gcTime: 0,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

export const useOpportunityOverlayState = () => {
    const queryClient = useQueryClient();
    const opportunityOverlayQuery = useOpportunityOverlayQuery();
    const setOpportunityOverlayState = (partialState: Partial<OpportunityOverlayState>) => {
        queryClient.setQueryData([OPPORTUNITY_OVERLAY_STATE_KEY], (data: OpportunityOverlayState) => ({
            ...data,
            ...partialState,
        }));
    };

    return {
        opportunityOverlayState: opportunityOverlayQuery.data ?? DEFAULT_OPPORTUNITY_OVERLAY_STATE,
        setOpportunityOverlayState,
        closeAllOverlays: () => setOpportunityOverlayState(DEFAULT_OPPORTUNITY_OVERLAY_STATE),
        openViewOpportunitySheet: (opportunityGuid: string) =>
            setOpportunityOverlayState({ opportunityGuid, sheetType: OpportunitySheetType.VIEW }),
        openDeleteOpportunityDialog: (opportunityGuid: string) =>
            setOpportunityOverlayState({ deleteOpportunityGuid: opportunityGuid }),
        closeDeleteOpportunityDialog: () => setOpportunityOverlayState({ deleteOpportunityGuid: null }),
    };
};
